import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { logService, LOG_CATEGORIES } from '@/services/logService'
import { recordingNativeService } from '@/services/recordingNativeService'
import { settingsService } from '@/services/settingsService'
import { showError, showSuccess } from '@/services/toastService'
import { calculateHaversineDistance } from '@/services/trackMetricsCalculationService'
import { UI_SETTING_KEYS } from '@/services/uiSettingsRegistry'
import type { RecordingDraft, RecordingPoint, RecordingState } from '@/types/recording'

import { useLibraryStore } from './libraryStore'

const DEFAULT_STATE: RecordingState = {
  status: 'idle',
  sessionId: null,
  startedAt: null,
  endedAt: null,
  pointCount: 0,
  segmentCount: 0,
  currentSegmentIndex: 0,
  lastPoint: null,
  autoPauseEnabled: false,
  autoPaused: false,
}

// Hysteresis threshold so GPS elevation jitter doesn't inflate the live ascent.
const ELEVATION_NOISE_THRESHOLD_M = 2

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}

export const useRecordingStore = defineStore('recording', () => {
  const state = ref<RecordingState>({ ...DEFAULT_STATE })
  const draft = ref<RecordingDraft | null>(null)
  const livePoints = ref<RecordingPoint[]>([])
  let isHydratingLivePoints = false
  const isModalOpen = ref(false)
  const isInitialized = ref(false)
  const isBusy = ref(false)
  const errorMessage = ref<string | null>(null)
  const autoPauseEnabled = ref(
    settingsService.getSetting<boolean>(UI_SETTING_KEYS.recordingAutoPauseEnabled) ?? false,
  )

  settingsService.subscribe(({ key, value, source }) => {
    if (
      source !== 'remote' ||
      key !== UI_SETTING_KEYS.recordingAutoPauseEnabled ||
      state.value.status !== 'idle'
    ) {
      return
    }
    autoPauseEnabled.value = value === true
  })

  const isSupported = computed(() => recordingNativeService.isSupported())
  const isActive = computed(() => state.value.status === 'active')
  const isPaused = computed(() => state.value.status === 'paused')
  const isAutoPaused = computed(() => state.value.autoPaused)
  const hasRecording = computed(() => isActive.value || isPaused.value)
  const hasStoppedDraft = computed(() => state.value.status === 'stopped' || !!draft.value)

  /** Live track coordinates grouped per segment ([lon, lat][][]) for the map overlay. */
  const liveSegments = computed<[number, number][][]>(() => {
    const segments: [number, number][][] = []
    for (const point of livePoints.value) {
      let segment = segments[point.segmentIndex]
      if (!segment) {
        segment = []
        segments[point.segmentIndex] = segment
      }
      segment.push([point.lon, point.lat])
    }
    return segments.filter((segment) => segment !== undefined && segment.length > 0)
  })

  /** Live distance in meters (haversine within segments; jumps across breaks don't count). */
  const liveDistanceMeters = computed(() => {
    let total = 0
    let prev: RecordingPoint | undefined
    for (const point of livePoints.value) {
      if (prev && prev.segmentIndex === point.segmentIndex) {
        total += calculateHaversineDistance(prev.lat, prev.lon, point.lat, point.lon)
      }
      prev = point
    }
    return total
  })

  /**
   * Cumulative live elevation gain in meters. A climb must rise
   * ELEVATION_NOISE_THRESHOLD_M above the running low before it counts, but a
   * confirmed climb is credited with its full baseline-to-peak extent —
   * including the sub-threshold tail at the top — so real ascent isn't
   * systematically undercounted. Elevation changes during breaks don't count.
   */
  const liveElevationGain = computed(() => {
    let gain = 0
    let segment = -1
    let baseline = 0
    let peak: number | null = null
    let hasBaseline = false

    for (const point of livePoints.value) {
      const ele = point.ele
      if (typeof ele !== 'number' || !Number.isFinite(ele)) continue
      if (!hasBaseline || point.segmentIndex !== segment) {
        if (peak !== null) gain += peak - baseline
        segment = point.segmentIndex
        baseline = ele
        peak = null
        hasBaseline = true
        continue
      }
      if (peak === null) {
        if (ele < baseline) {
          baseline = ele
        } else if (ele - baseline >= ELEVATION_NOISE_THRESHOLD_M) {
          peak = ele
        }
      } else if (ele > peak) {
        peak = ele
      } else if (peak - ele >= ELEVATION_NOISE_THRESHOLD_M) {
        gain += peak - baseline
        baseline = ele
        peak = null
      }
    }

    if (peak !== null) gain += peak - baseline
    return gain
  })

  /** First/last fix time (ms) per segment, used to subtract breaks from the elapsed time. */
  const liveSegmentTimeBounds = computed<{ start: number; end: number }[]>(() => {
    const bounds: ({ start: number; end: number } | undefined)[] = []
    for (const point of livePoints.value) {
      const existing = bounds[point.segmentIndex]
      if (!existing) {
        bounds[point.segmentIndex] = { start: point.time, end: point.time }
      } else {
        existing.start = Math.min(existing.start, point.time)
        existing.end = Math.max(existing.end, point.time)
      }
    }
    return bounds.filter((entry) => entry !== undefined)
  })

  /**
   * Elapsed (wall-clock, incl. breaks) vs active (breaks excluded) duration at `nowMs`.
   * A function rather than a computed so callers can drive it from a ticking clock.
   */
  const getLiveDurations = (nowMs: number): { elapsedMs: number; activeMs: number } => {
    const startedAt = state.value.startedAt
    if (!startedAt) return { elapsedMs: 0, activeMs: 0 }
    const endedAt = state.value.endedAt ?? nowMs
    const elapsedMs = Math.max(0, endedAt - startedAt)

    const bounds = liveSegmentTimeBounds.value
    let breakMs = 0
    for (let i = 1; i < bounds.length; i++) {
      breakMs += Math.max(0, bounds[i].start - bounds[i - 1].end)
    }
    if (isPaused.value || isAutoPaused.value) {
      const pauseStart = bounds[bounds.length - 1]?.end ?? startedAt
      breakMs += Math.max(0, endedAt - pauseStart)
    }

    return { elapsedMs, activeMs: Math.max(0, elapsedMs - breakMs) }
  }

  const openModal = () => {
    isModalOpen.value = true
  }

  const closeModal = () => {
    isModalOpen.value = false
  }

  // Keep the live map-overlay points in sync: append the incremental lastPoint when possible,
  // otherwise (recovered session, missed state events) hydrate the full point list from the draft.
  const syncLivePoints = async (nextState: RecordingState) => {
    if (nextState.status === 'idle') {
      livePoints.value = []
      return
    }

    const points = livePoints.value
    const lastKnown = points[points.length - 1]
    const incoming = nextState.lastPoint

    const isInSync =
      nextState.pointCount === points.length &&
      (!incoming || (lastKnown !== undefined && lastKnown.time === incoming.time))
    if (isInSync) return

    if (
      incoming &&
      nextState.pointCount === points.length + 1 &&
      (lastKnown === undefined || incoming.time >= lastKnown.time)
    ) {
      livePoints.value = [...points, incoming]
      return
    }

    if (draft.value && draft.value.sessionId === nextState.sessionId) {
      livePoints.value = draft.value.points
      return
    }

    if (isHydratingLivePoints) return
    isHydratingLivePoints = true
    try {
      const hydratedDraft = await recordingNativeService.readDraft()
      if (hydratedDraft) {
        livePoints.value = hydratedDraft.points
      }
    } catch (error) {
      logService.warn(LOG_CATEGORIES.GENERAL, 'Failed to hydrate live recording points', { error })
    } finally {
      isHydratingLivePoints = false
    }
  }

  const setState = async (nextState: RecordingState) => {
    state.value = nextState
    if (nextState.status !== 'idle') {
      autoPauseEnabled.value = nextState.autoPauseEnabled
    }

    if (nextState.status === 'stopped') {
      draft.value = await recordingNativeService.readDraft()
    } else if (nextState.status === 'idle') {
      draft.value = null
    }

    await syncLivePoints(nextState)
    await recordingNativeService.resumeForegroundWriterIfNeeded(nextState)
  }

  const initialize = async () => {
    if (isInitialized.value || !isSupported.value) {
      return
    }

    try {
      await recordingNativeService.listenForStateChanges((nextState) => {
        void setState(nextState)
      })

      const recoveredState = await recordingNativeService.getState()
      await setState(recoveredState)

      if (recoveredState.status === 'stopped') {
        openModal()
      }
    } catch (error) {
      errorMessage.value = getErrorMessage(error)
      logService.error(LOG_CATEGORIES.GENERAL, 'Failed to initialize recording state', { error })
    } finally {
      isInitialized.value = true
    }
  }

  const start = async () => {
    if (!isSupported.value) {
      showError('Track recording is only available in the Tauri mobile app.')
      return
    }

    isBusy.value = true
    errorMessage.value = null
    try {
      await setState(await recordingNativeService.start(autoPauseEnabled.value))
    } catch (error) {
      errorMessage.value = getErrorMessage(error)
      showError(`Failed to start recording: ${errorMessage.value}`)
      logService.error(LOG_CATEGORIES.GENERAL, 'Failed to start recording', { error })
    } finally {
      isBusy.value = false
    }
  }

  const pause = async () => {
    if (!hasRecording.value) return

    isBusy.value = true
    errorMessage.value = null
    try {
      await setState(await recordingNativeService.pause())
    } catch (error) {
      errorMessage.value = getErrorMessage(error)
      showError(`Failed to pause recording: ${errorMessage.value}`)
      logService.error(LOG_CATEGORIES.GENERAL, 'Failed to pause recording', { error })
    } finally {
      isBusy.value = false
    }
  }

  const resume = async () => {
    if (!isPaused.value) return

    isBusy.value = true
    errorMessage.value = null
    try {
      await setState(await recordingNativeService.resume())
    } catch (error) {
      errorMessage.value = getErrorMessage(error)
      showError(`Failed to resume recording: ${errorMessage.value}`)
      logService.error(LOG_CATEGORIES.GENERAL, 'Failed to resume recording', { error })
    } finally {
      isBusy.value = false
    }
  }

  const setAutoPauseEnabled = async (enabled: boolean) => {
    autoPauseEnabled.value = enabled
    settingsService.saveSetting(UI_SETTING_KEYS.recordingAutoPauseEnabled, enabled)

    if (!isSupported.value || state.value.status === 'idle' || state.value.status === 'stopped') {
      return
    }

    isBusy.value = true
    errorMessage.value = null
    try {
      await setState(await recordingNativeService.setAutoPauseEnabled(enabled))
    } catch (error) {
      errorMessage.value = getErrorMessage(error)
      showError(`Failed to update auto pause: ${errorMessage.value}`)
      logService.error(LOG_CATEGORIES.GENERAL, 'Failed to update recording auto pause', { error })
    } finally {
      isBusy.value = false
    }
  }

  const stopAndSave = async () => {
    if (!hasRecording.value && !hasStoppedDraft.value) return

    isBusy.value = true
    errorMessage.value = null
    try {
      const stoppedDraft = draft.value ?? (await recordingNativeService.stop())
      draft.value = stoppedDraft

      const libraryStore = useLibraryStore()
      const tourId = await libraryStore.createTourFromRecordingDraft(stoppedDraft)
      if (!tourId) return

      await setState(await recordingNativeService.clearDraft(stoppedDraft.sessionId))
      showSuccess('Recording saved to Library.')
      closeModal()
    } catch (error) {
      errorMessage.value = getErrorMessage(error)
      showError(`Failed to save recording: ${errorMessage.value}`)
      logService.error(LOG_CATEGORIES.GENERAL, 'Failed to save recording', { error })
    } finally {
      isBusy.value = false
    }
  }

  const stopOnly = async () => {
    if (!hasRecording.value) return

    isBusy.value = true
    errorMessage.value = null
    try {
      draft.value = await recordingNativeService.stop()
      state.value = {
        ...state.value,
        status: 'stopped',
        endedAt: draft.value.endedAt,
        pointCount: draft.value.pointCount,
        segmentCount: draft.value.segmentCount,
      }
    } catch (error) {
      errorMessage.value = getErrorMessage(error)
      showError(`Failed to stop recording: ${errorMessage.value}`)
      logService.error(LOG_CATEGORIES.GENERAL, 'Failed to stop recording', { error })
    } finally {
      isBusy.value = false
    }
  }

  const discard = async () => {
    if (!hasRecording.value && !hasStoppedDraft.value) return

    isBusy.value = true
    errorMessage.value = null
    try {
      await setState(await recordingNativeService.discard())
      closeModal()
    } catch (error) {
      errorMessage.value = getErrorMessage(error)
      showError(`Failed to discard recording: ${errorMessage.value}`)
      logService.error(LOG_CATEGORIES.GENERAL, 'Failed to discard recording', { error })
    } finally {
      isBusy.value = false
    }
  }

  return {
    state,
    draft,
    livePoints,
    liveSegments,
    liveDistanceMeters,
    liveElevationGain,
    getLiveDurations,
    isModalOpen,
    isInitialized,
    isBusy,
    errorMessage,
    autoPauseEnabled,
    isSupported,
    isActive,
    isPaused,
    isAutoPaused,
    hasRecording,
    hasStoppedDraft,
    initialize,
    openModal,
    closeModal,
    start,
    pause,
    resume,
    setAutoPauseEnabled,
    stopOnly,
    stopAndSave,
    discard,
  }
})
